import { Facebook, Instagram, Linkedin, Heart, Plane } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { publicUrl } from '../utils/publicUrl'

// WhatsApp Icon Component
const WhatsApp = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
)

const navigation = {
  main: [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Holiday Packages', href: '/packages' },
    { name: 'Contact us', href: '/contact' },
  ],
}

export default function Footer() {
  const { franchiseData, loading } = useFranchiseData()

  if (loading || !franchiseData) {
    return (
      <footer className="bg-indojet-light border-t border-gray-200">
        <div className="mx-auto max-w-7xl overflow-hidden px-6 py-12 sm:py-16 lg:px-8">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 rounded w-48 mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-32"></div>
          </div>
        </div>
      </footer>
    )
  }

  const socialLinks = [
    {
      name: 'Facebook',
      href: franchiseData.facebook || '#',
      icon: Facebook,
    },
    {
      name: 'WhatsApp',
      href: franchiseData.whatsapp ? `https://wa.me/${franchiseData.whatsapp}` : '#',
      icon: WhatsApp,
    },
    {
      name: 'Instagram',
      href: franchiseData.instagram || '#',
      icon: Instagram,
    },
  ].filter((link) => link.href !== '#') // Only show links that exist

  return (
    <footer className="bg-[#b6bcbc] text-gray-800">
      <div className="mx-auto max-w-7xl overflow-hidden px-6 py-6 sm:py-8 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:items-start">
          {/* Brand Section */}
          <div className="lg:col-span-1 flex items-center justify-center lg:justify-start mt-4">
            <img
              src={publicUrl('indojet-travel-logo-6.png')}
              alt="IndoJet Travel Logo"
              className="w-auto h-24 sm:h-28 md:h-32 lg:h-[260px]"
            />
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-1">
            <h3 className="text-base font-bold text-gray-800 mb-3 uppercase tracking-wider">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {navigation.main.map((item) => (
                <li key={item.name}>
                  <a
                    href={item.href}
                    className="text-gray-700 hover:text-[#182c78] transition-colors text-base"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Details */}
          <div className="lg:col-span-1">
            <h3 className="text-base font-bold text-gray-800 mb-3 uppercase tracking-wider">
              Contact Info
            </h3>
            <div className="space-y-2">
              <div className="flex items-start space-x-2 text-sm sm:text-base text-gray-700">
                <span className="text-[#182c78] flex-shrink-0 mt-0.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline h-4 w-4 sm:h-5 sm:w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a2 2 0 011.7.95l1.54 2.6a2 2 0 01-.17 2.18l-1.1 1.65a11.042 11.042 0 005.516 5.516l1.65-1.1a2 2 0 012.18-.17l2.6 1.54a2 2 0 01.95 1.7V19a2 2 0 01-2 2h-1C7.82 21 3 16.18 3 10V9a2 2 0 012-2z"
                    />
                  </svg>
                </span>
                <span className="break-words">{franchiseData.telephone}</span>
              </div>
              <div className="flex items-start space-x-2 text-sm sm:text-base text-gray-700">
                <span className="text-[#182c78] mt-1 flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline h-4 w-4 sm:h-5 sm:w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 12H8m8 0a4 4 0 11-8 0 4 4 0 018 0zm0 0v1a4 4 0 01-8 0v-1"
                    />
                  </svg>
                </span>
                <div className="break-words">
                  <div>{franchiseData.email}</div>
                  <div>indojet.team@discovery.co.za</div>
                </div>
              </div>
              {franchiseData.address && (
                <div className="flex items-start space-x-2 text-sm sm:text-base text-gray-700">
                  <span className="text-[#182c78] mt-1 flex-shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="inline h-4 w-4 sm:h-5 sm:w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 21l-4.243-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </span>
                  <span className="break-words">
                    {franchiseData.address.split('\r\n').map((line, index) => (
                      <span key={index}>
                        {line}
                        {index < franchiseData.address.split('\r\n').length - 1 && <br />}
                      </span>
                    ))}
                  </span>
                </div>
              )}
              <div className="flex items-start space-x-2 text-sm sm:text-base text-gray-700">
                <span className="text-[#182c78] mt-1 flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline h-4 w-4 sm:h-5 sm:w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </span>
                <div>
                  <div className="font-semibold">Working Hours</div>
                  <div>24 Hours</div>
                </div>
              </div>
              <div className="flex items-start space-x-2 text-sm sm:text-base text-gray-700">
                <span className="text-[#182c78] mt-1 flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline h-4 w-4 sm:h-5 sm:w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a2 2 0 011.7.95l1.54 2.6a2 2 0 01-.17 2.18l-1.1 1.65a11.042 11.042 0 005.516 5.516l1.65-1.1a2 2 0 012.18-.17l2.6 1.54a2 2 0 01.95 1.7V19a2 2 0 01-2 2h-1C7.82 21 3 16.18 3 10V9a2 2 0 012-2z"
                    />
                  </svg>
                </span>
                <div className="break-words">
                  <div className="font-semibold">Australia Office details:</div>
                  <div>Cell: +61 4 505 87 505</div>
                  <div>Address: 302/62A Dover Road, Rose Bay, Sydney, Australia</div>
                  <div>Iata #: 02308250</div>
                </div>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="lg:col-span-1">
            <h3 className="text-base font-bold text-gray-800 mb-3 uppercase tracking-wider">
              Follow Us
            </h3>
            <div className="flex flex-col space-y-2">
              {socialLinks.map((social) => {
                const Icon = social.icon
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 text-gray-700 hover:text-[#182c78] transition-colors text-base"
                  >
                    <Icon className="h-5 w-5" />
                    <span>{social.name}</span>
                  </a>
                )
              })}
            </div>
          </div>

        </div>

        {/* Bottom Section */}
        <div className="mt-6 border-t border-gray-400 pt-4">
          <div className="flex items-center justify-center">
            <p className="text-xs text-gray-600 text-center">
              Copyright © 2024 Indojet Travel Sandton. All rights reserved | Developed by Billie and Code
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
